import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { compact, flatten } from 'lodash';

import {
  faGripHorizontal,
  faFileContract,
  faInfoCircle,
  IconDefinition,
  faPlus,
  faKey,
  faPhone,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import { faProductHunt } from '@fortawesome/free-brands-svg-icons';
import {
  selectIsFrontline,
  selectIsInsurer,
  selectIsCompany,
  selectIsCustomer,
  selectIsHospital,
} from '@components/Auth';
import { routeInsurancePolicyBuyNewPage } from '@components/InsurancePolicyBuyRoute';
import {
  routeUserDashboardInsurancePolicyListPage,
  routeUserDashboardMobileNumberPage,
  routeUserDashboardPasswordPage,
  routeUserDashboardInsuranceClaimListPage,
} from './route';

type DashboardItemKey =
  | 'dashboard'
  | 'oldDashboard'
  | 'billing'
  | 'policy'
  | 'insurancePolicy'
  | 'insuranceClaim'
  | 'password'
  | 'mobileNumber'
  | 'personalInfo'
  | 'otpLink'
  | 'searchInsurancePolicyUser'

type DashboardItem = {
  key: DashboardItemKey;
  label: string;
  to: string;
  icon: IconDefinition;
};

export const useUserDashboard = () => {
  const { t } = useTranslation();
  const isCustomer = useSelector(selectIsCustomer);
  const isFrontline = useSelector(selectIsFrontline);
  const isInsurer = useSelector(selectIsInsurer);
  const isCompany = useSelector(selectIsCompany);
  const isHospital = useSelector(selectIsHospital);
  const dashboardHref = '/user-dashboard';
  const policyHref = '/user-dashboard/policy';
  const searchInsurnacePolicyUserHref = '/user-dashboard/search-insurance-policy-user';
  const personalInfoHref = '/user-dashboard/personal-info';

  const items = useMemo(() => {
    return flatten(
      compact([
        isCustomer && [
          {
            key: 'dashboard',
            label: t('common:dashboard'),
            to: dashboardHref,
            icon: faGripHorizontal,
          },
        ],
        !isHospital && [
          {
            key: 'insurancePolicy',
            label: t('common:policy'),
            to: routeUserDashboardInsurancePolicyListPage(),
            icon: faProductHunt,
          },
        ],
        isFrontline && [
          {
            key: 'newInsurancePolicy',
            label: t('common:newPolicy'),
            to: routeInsurancePolicyBuyNewPage(),
            icon: faPlus,
          },
        ],
        (isFrontline || isInsurer || isCompany) && [
          {
            key: 'searchInsurancePolicyUser',
            label: t('common:searchInsurancePolicyHolder'),
            to: searchInsurnacePolicyUserHref,
            icon: faMagnifyingGlass,
          },
        ],
        [
          {
            key: 'insuranceClaim',
            label: t('common:claim'),
            to: routeUserDashboardInsuranceClaimListPage(),
            icon: faFileContract,
          },
        ],
        {
          key: 'password',
          label: t('common:password'),
          to: routeUserDashboardPasswordPage(),
          icon: faKey,
        },
        {
          key: 'mobileNumber',
          label: t('user.mobileNumber'),
          to: routeUserDashboardMobileNumberPage(),
          icon: faPhone,
        },
        {
          key: 'personalInfo',
          label: t('common:personalInfo'),
          to: personalInfoHref,
          icon: faInfoCircle,
        },
        isFrontline && [
          {
            key: 'policy',
            label: `(OLD) ${t('common:policy')}`,
            to: policyHref,
            icon: faProductHunt,
          },
        ],
      ]) as DashboardItem[],
    );
  }, [isCustomer, isFrontline, isInsurer, isCompany, isHospital]);

  return {
    items,
  };
};

export const useActiveKey = (): DashboardItemKey => {
  const { pathname } = useLocation();
  if (matchPath('/user-dashboard/insurance-policy/*', pathname)) return 'insurancePolicy';
  if (matchPath('/user-dashboard/policy/*', pathname)) return 'policy';
  if (matchPath('/user-dashboard/search-insurance-policy-user/*', pathname)) return 'searchInsurancePolicyUser';
  if (matchPath('/user-dashboard/billing/*', pathname)) return 'billing';
  if (matchPath('/user-dashboard/insurance-claim/*', pathname)) return 'insuranceClaim';
  if (matchPath('/user-dashboard/password/*', pathname)) return 'password';
  if (matchPath('/user-dashboard/mobile-number/*', pathname)) return 'mobileNumber';
  if (matchPath('/user-dashboard/personal-info/*', pathname)) return 'personalInfo';
  if (matchPath('/user-dashboard/otp-link/*', pathname)) return 'otpLink';
  if (matchPath('/user-dashboard/old-dashboard/*', pathname)) return 'oldDashboard';
  return 'dashboard';
};
